/**
 * The closing-cost request and cash-to-close arithmetic shared by the
 * ClosingCostEstimator panel and the listing dialog's Costs row subtitle, so
 * the subtitle always shows the figure the panel opens on.
 */
export type ClosingCostPaymentMethod = 'Finance' | 'Cash';

export const DEFAULT_DOWN_PAYMENT_PERCENT = 20;

/** An assumable home opens on Cash (the buyer takes over the loan); others on Finance. */
export const initialPaymentMethod = (
  isAssumable: boolean
): ClosingCostPaymentMethod => (isAssumable ? 'Cash' : 'Finance');

export interface ClosingCostQueryInput {
  propertyPrice: number;
  propertyTax?: number;
  paymentMethod: ClosingCostPaymentMethod;
  downPaymentPercent: number;
  isAssumable: boolean;
}

/**
 * Params for GET /buyer-closing-costs. The listing headline's buying-power
 * estimate assumes an inspection (see utils/buyerBrokerFee.ts), so the same
 * term is sent here and the fee in the breakdown matches it.
 */
export const closingCostQuery = ({
  propertyPrice,
  propertyTax = 0,
  paymentMethod,
  downPaymentPercent,
  isAssumable,
}: ClosingCostQueryInput) => ({
  propertyPrice,
  propertyTax,
  paymentMethod,
  downPaymentPercent,
  isAssumable,
  hasInspection: true,
  hasHomeSaleContingency: false,
});

/**
 * Cash the buyer brings to closing: the down payment (the whole price for
 * cash, the seller's equity for an assumable) plus closing costs.
 */
export const cashToCloseFor = ({
  propertyPrice,
  paymentMethod,
  downPaymentPercent,
  isAssumable,
  openMortgageBalance = 0,
  closingCostsTotal = 0,
}: {
  propertyPrice: number;
  paymentMethod: ClosingCostPaymentMethod;
  downPaymentPercent: number;
  isAssumable: boolean;
  openMortgageBalance?: number;
  closingCostsTotal?: number;
}): number => {
  let downPayment = propertyPrice * (downPaymentPercent / 100);
  if (isAssumable) {
    downPayment = propertyPrice - openMortgageBalance;
  } else if (paymentMethod === 'Cash') {
    downPayment = propertyPrice;
  }
  return downPayment + closingCostsTotal;
};

/**
 * Closing costs the buyer pays: the service's total less Homepoint's fee,
 * which the purchase contract has the seller pay, so it is shown on its own
 * line and never inside the buyer's closing costs or cash to close.
 */
export interface BuyerClosingCostsTotals {
  total?: number;
  otherFees?: { brokerFee?: number };
}

export const buyerPaidClosingCosts = (
  costs: BuyerClosingCostsTotals
): number => (costs.total ?? 0) - (costs.otherFees?.brokerFee ?? 0);
